// client/src/components/doctors/PatientManagement/PatientDetails.jsx
// FINAL FIXED VERSION

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import doctorService from '../../../services/doctorService';
import '../../../styles/components/doctor/PatientDetails.css';

const PatientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('registration');

  // Journal-related states
  const [journalData, setJournalData] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState(null);

  // Mood check-ins related states
  const [moodData, setMoodData] = useState(null);
  const [moodLoading, setMoodLoading] = useState(false);
  const [moodError, setMoodError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(7);

  // Mood SVG URLs and colors
  const moodSvgUrls = {
    great: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_Great_anfp2e.svg',
    good: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_good_ldcpo4.svg',
    okay: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_okay_tcf9cp.svg',
    struggling: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_struggling_regocn.svg',
    upset: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_upset_pqskxp.svg'
  };

  const moodColors = {
    great: '#4CAF50',
    good: '#8BC34A', 
    okay: '#FFC107',
    struggling: '#FF9800',
    upset: '#F44336'
  };

  // Helper function to safely display values
  const displayValue = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null') {
      return 'Not provided';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    return value.toString().trim() || 'Not provided';
  };

  // Fetch patient details
  useEffect(() => {
    const fetchPatientDetails = async () => {
      try {
        setLoading(true);
        const response = await doctorService.getPatient(id);
        
        console.log('🔍 FULL API RESPONSE:', response);
        
        if (response.success && response.data) {
          setPatient(response.data);
        } else if (response.patient) {
          setPatient(response.patient);
        } else if (response._id) {
          setPatient(response);
        } else {
          console.error('❌ Unexpected response structure:', response);
          setError('Patient not found or unexpected response format');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Error fetching patient details:', error);
        setError('Failed to load patient details. Please try again.');
        setLoading(false);
      }
    };

    if (id) {
      fetchPatientDetails();
    }
  }, [id]);

  // 🔥 FIXED: Use the working endpoint from debug results
  const fetchPatientJournalData = async () => {
    try {
      setJournalLoading(true);
      setJournalError(null);
      
      console.log('🔍 Fetching journal data for patient ID:', id);
      
      // ✅ Use the exact working endpoint from debug results
      const response = await doctorService.getJournalEntries({ 
        patient: id  // This parameter works as confirmed by debug
      });
      
      console.log('📊 Journal response:', response);
      console.log('📊 Response type:', typeof response);
      console.log('📊 Response structure:', {
        hasSuccess: !!response?.success,
        hasData: !!response?.data,
        dataType: typeof response?.data,
        isDataArray: Array.isArray(response?.data),
        dataLength: response?.data?.length || 0
      });
      
      // Handle the response exactly as debug showed it works
      if (response && response.success && Array.isArray(response.data)) {
        console.log(`✅ SUCCESS: Found ${response.data.length} journal entries`);
        setJournalData(response.data);
      } else if (Array.isArray(response)) {
        console.log(`✅ SUCCESS: Found ${response.length} journal entries (direct array)`);
        setJournalData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        console.log(`✅ SUCCESS: Found ${response.data.length} journal entries (nested)`);
        setJournalData(response.data);
      } else {
        console.log('📝 No journal entries found in response');
        console.log('📝 Full response for debugging:', response);
        setJournalData([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching patient journal data:', error);
      setJournalError('Failed to load journal entries');
      setJournalData([]);
    } finally {
      setJournalLoading(false);
    }
  };

  // 🔥 NEW: Fallback method that manually calls the working endpoint
  const fetchPatientJournalDataDirect = async () => {
    try {
      setJournalLoading(true);
      setJournalError(null);
      
      console.log('🔍 Using direct API call (working endpoint)');
      
      const token = localStorage.getItem('token');
      const tenantStr = localStorage.getItem('tenant');
      let tenantId = null;
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantId = tenant._id;
      }

      // Use the exact URL that worked in debug
      const baseURL = process.env.REACT_APP_API_URL || 'https://neurolex-platform-9b4c40c0e2da.herokuapp.com/api';
      const url = `${baseURL}/journal/doctor?patient=${id}`;
      
      console.log('🌐 Direct API URL:', url);
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      if (tenantId) {
        headers['x-tenant-id'] = tenantId;
        headers['X-Tenant-ID'] = tenantId;
      }
      
      const response = await fetch(url, { headers });
      const data = await response.json();
      
      console.log('📊 Direct API response:', data);
      console.log('📊 Status:', response.status);
      
      if (response.status === 200 && data.success && Array.isArray(data.data)) {
        console.log(`✅ DIRECT SUCCESS: Found ${data.data.length} journal entries`);
        setJournalData(data.data);
      } else {
        console.log('📝 Direct call: No entries found');
        setJournalData([]);
      }
      
    } catch (error) {
      console.error('❌ Direct API call failed:', error);
      setJournalError('Failed to load journal entries via direct API call');
      setJournalData([]);
    } finally {
      setJournalLoading(false);
    }
  };

  // Fetch patient mood data
  const fetchPatientMoodData = async () => {
    try {
      setMoodLoading(true);
      setMoodError(null);
      
      const response = await doctorService.getIndividualPatientMoodAnalytics(id, selectedDays);
      
      if (response.success && response.data) {
        setMoodData(response.data);
      } else {
        setMoodData(null);
      }
    } catch (error) {
      console.error('Error fetching patient mood data:', error);
      setMoodError('Failed to load mood check-in data');
      setMoodData(null);
    } finally {
      setMoodLoading(false);
    }
  };

  // Fetch journal data when tab becomes active
  useEffect(() => {
    if (activeTab === 'journals' && patient) {
      fetchPatientJournalData();
    }
  }, [activeTab, patient]);

  // Fetch mood data when tab becomes active or days filter changes
  useEffect(() => {
    if (activeTab === 'mood-checkins' && patient) {
      fetchPatientMoodData();
    }
  }, [activeTab, patient, selectedDays]);

  const handleBack = () => {
    navigate('/doctor/patients');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
        </div>
        <button className="primary-button" onClick={handleBack}>
          Back to Patients
        </button>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="error-container">
        <div className="error-alert">
          <span className="error-icon">❌</span>
          <span className="error-message">Patient not found</span>
        </div>
        <button className="primary-button" onClick={handleBack}>
          Back to Patients
        </button>
      </div>
    );
  }

  return (
    <div className="patient-details-container">
      {/* Header */}
      <div className="patient-header">
        <div className="header-left-">
          <button className="back-button" onClick={handleBack}>
            <span className="back-icon">←</span>
          </button>
          <div className="patient-info">
            <h1 className="patient-name-">
              {patient.firstName} {patient.lastName}
            </h1>
            <p className="patient-email-">{patient.email}</p>
          </div>
        </div>
        <div className="header-right-">
          <div className="status-badge active">
            ● Active
          </div>
          <div className="registration-date">
            Registration Date: {patient.createdAt ? new Date(patient.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : 'Unknown'}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'registration' ? 'active' : ''}`}
          onClick={() => setActiveTab('registration')}
        >
          Registration
        </button>
        <button 
          className={`tab-button ${activeTab === 'onboarding' ? 'active' : ''}`}
          onClick={() => setActiveTab('onboarding')}
        >
          Onboarding
        </button>
        <button 
          className={`tab-button ${activeTab === 'mood-checkins' ? 'active' : ''}`}
          onClick={() => setActiveTab('mood-checkins')}
        >
          Mood Check-ins
        </button>
        <button 
          className={`tab-button ${activeTab === 'journals' ? 'active' : ''}`}
          onClick={() => setActiveTab('journals')}
        >
          Journals
        </button>
        <button 
          className={`tab-button ${activeTab === 'appointments' ? 'active' : ''} disabled`}
          onClick={() => setActiveTab('appointments')}
          disabled
        >
          Doctor & Appointments
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'registration' && (
          <RegistrationTab patient={patient} displayValue={displayValue} />
        )}
        {activeTab === 'onboarding' && (
          <OnboardingTab patient={patient} displayValue={displayValue} />
        )}
        {activeTab === 'mood-checkins' && (
          <MoodCheckinsTab 
            patient={patient}
            patientId={id}  
            navigate={navigate}   
            moodData={moodData}
            moodLoading={moodLoading}
            moodError={moodError}
            selectedDays={selectedDays}
            setSelectedDays={setSelectedDays}
            fetchPatientMoodData={fetchPatientMoodData}
            moodSvgUrls={moodSvgUrls}
            moodColors={moodColors}
          />
        )}
        {activeTab === 'journals' && (
          <JournalsTab 
            patient={patient} 
            displayValue={displayValue}
            journalData={journalData}
            journalLoading={journalLoading}
            journalError={journalError}
            fetchPatientJournalData={fetchPatientJournalData}
            fetchPatientJournalDataDirect={fetchPatientJournalDataDirect}
            navigate={navigate}
          />
        )}
        {activeTab === 'appointments' && (
          <div className="coming-soon">
            <h3>Doctor & Appointments</h3>
            <p>This section will be implemented later.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const JournalsTab = ({ 
  patient, 
  displayValue, 
  journalData, 
  journalLoading, 
  journalError, 
  fetchPatientJournalData,
  fetchPatientJournalDataDirect,
  navigate
}) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSentimentColor = (sentiment) => {
    let sentimentValue = '';
    
    if (typeof sentiment === 'string') {
      sentimentValue = sentiment.toLowerCase();
    } else if (sentiment && typeof sentiment === 'object') {
      sentimentValue = (sentiment.type || sentiment.sentiment || '').toString().toLowerCase();
    } else {
      sentimentValue = '';
    }
    
    switch (sentimentValue) {
      case 'positive':
        return '#4CAF50';
      case 'negative':
        return '#F44336';
      case 'neutral':
        return '#FFD700';
      default:
        return '#9E9E9E';
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (journalLoading) {
    return (
      <div className="doctor-patient-journal-tab-panel">
        <div className="doctor-patient-journal-loading-container">
          <div className="doctor-patient-journal-loading-spinner"></div>
          <p>Loading journal entries for {patient.firstName}...</p>
        </div>
      </div>
    );
  }

  if (journalError) {
    return (
      <div className="doctor-patient-journal-tab-panel">
        <div className="doctor-patient-journal-error-container">
          <div className="doctor-patient-journal-error-alert">
            <span className="doctor-patient-journal-error-icon">⚠️</span>
            <span className="doctor-patient-journal-error-message">{journalError}</span>
          </div>
          <div className="doctor-patient-journal-error-actions">
            <button className="doctor-patient-journal-retry-button" onClick={fetchPatientJournalData}>
              🔄 Try Again
            </button>
            <button className="doctor-patient-journal-retry-button secondary" onClick={fetchPatientJournalDataDirect}>
              🔧 Try Direct API Call
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-patient-journal-tab-panel">
      <div className="doctor-patient-journal-content">
        <div className="doctor-patient-journal-management-header">
          <h1 className="doctor-patient-journal-title">Journal Entries</h1>
          <p className="doctor-patient-journal-subtitle">View and track {patient.firstName}'s personal journal entries and emotional insights</p>
        </div>

        {journalData.length === 0 ? (
          <div className="doctor-patient-journal-empty-state">
            <h3 className="doctor-patient-journal-empty-title">No Journal Entries Found</h3>
            <p className="doctor-patient-journal-empty-text">{patient.firstName} hasn't created any journal entries yet.</p>
          </div>
        ) : (
          <>
            {/* Journal Entries Table */}
            <div className="doctor-patient-journal-entries-table">
              {/* Table Header */}
              <div className="doctor-patient-journal-table-header">
                <div className="doctor-patient-journal-header-cell">Date</div>
                <div className="doctor-patient-journal-header-cell">Time</div>
                <div className="doctor-patient-journal-header-cell">Journal Entry</div>
                <div className="doctor-patient-journal-header-cell">Sentiment</div>
                <div className="doctor-patient-journal-header-cell">Emotions Detected</div>
                <div className="doctor-patient-journal-header-cell">Actions</div>
              </div>

              {/* Table Rows */}
              {journalData.map((entry, index) => (
                <div key={entry._id || index} className="doctor-patient-journal-table-row">
                  <div className="doctor-patient-journal-table-cell">
                    {formatDate(entry.createdAt || entry.timestamp)}
                  </div>
                  <div className="doctor-patient-journal-table-cell">
                    {formatTime(entry.createdAt || entry.timestamp)}
                  </div>
                  <div className="doctor-patient-journal-table-cell">
                    <div className="doctor-patient-journal-entry-preview">
                      <div className="doctor-patient-journal-entry-title">
                        {entry.title || entry.templateName || 'Untitled Entry'}
                      </div>
                      <div className="doctor-patient-journal-entry-content">
                        {truncateText(entry.content || entry.text)}
                      </div>
                    </div>
                  </div>
                  <div className="doctor-patient-journal-table-cell">
                    <div className="doctor-patient-journal-sentiment-indicator">
                      <div 
                        className="doctor-patient-journal-sentiment-dot"
                        style={{ 
                          backgroundColor: getSentimentColor(entry.sentiment || entry.sentimentAnalysis?.sentiment)
                        }}
                      ></div>
                      <span className="doctor-patient-journal-sentiment-text">
                        {(() => {
                          if (typeof entry.sentiment === 'string') {
                            return entry.sentiment;
                          } else if (entry.sentiment && typeof entry.sentiment === 'object') {
                            return entry.sentiment.type || entry.sentiment.sentiment || 'Unknown';
                          } else if (entry.sentimentAnalysis?.sentiment) {
                            return entry.sentimentAnalysis.sentiment;
                          } else {
                            return 'Unknown';
                          }
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="doctor-patient-journal-table-cell">
                    <div className="doctor-patient-journal-emotion-tags">
                      {entry.emotions || entry.sentimentAnalysis?.emotions ? (
                        (entry.emotions || entry.sentimentAnalysis.emotions).slice(0, 3).map((emotion, idx) => (
                          <span key={idx} className="doctor-patient-journal-emotion-tag">
                            {emotion}
                          </span>
                        ))
                      ) : (
                        <span className="doctor-patient-journal-no-emotions">
                          No emotions detected
                        </span>
                      )}
                      {(entry.emotions || entry.sentimentAnalysis?.emotions)?.length > 3 && (
                        <span className="doctor-patient-journal-emotion-tag doctor-patient-journal-emotion-more">
                          +{(entry.emotions || entry.sentimentAnalysis.emotions).length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="doctor-patient-journal-table-cell">
                    <div className="doctor-patient-journal-actions-cell">
                      <div className="doctor-patient-journal-action-buttons">
                        <div
                              className="doctor-patient-journal-btn-icon doctor-patient-journal-btn-view"
                              onClick={() => {
                                navigate(`/doctor/journal-entries/${entry._id}`);
                              }}
                              title="View Entry"
                            >
                          <svg className="doctor-patient-journal-icon-view" viewBox="0 0 24 24" fill="none">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination (if needed) */}
            {journalData.length > 10 && (
              <div className="doctor-patient-journal-pagination">
                <div className="doctor-patient-journal-pagination-info">
                  Showing {journalData.length} entries
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Registration Tab Component (unchanged)
const RegistrationTab = ({ patient, displayValue }) => {
  return (
    <div className="tab-panel">
      <div className="section-card">
        <h3 className="section-title">Profile Information</h3>
        
        <div className="profile-section">
          <div className="profile-photo-section">
            <div className="profile-photo">
              {patient.profilePicture && patient.profilePicture !== 'default-profile.png' ? (
                <img 
                  src={patient.profilePicture} 
                  alt="Profile" 
                  className="profile-image"
                />
              ) : (
                <div className="default-avatar">
                  {patient.firstName?.[0] || 'P'}
                </div>
              )}
            </div>
            <p className="photo-label">Profile Photo</p>
          </div>

          <div className="profile-details">
            <div className="detail-row">
              <div className="detail-group">
                <label className="detail-label">First Name</label>
                <div className="detail-value">{displayValue(patient.firstName)}</div>
              </div>
              <div className="detail-group">
                <label className="detail-label">Last Name</label>
                <div className="detail-value">{displayValue(patient.lastName)}</div>
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-group">
                <label className="detail-label">Email Address</label>
                <div className="detail-value">{displayValue(patient.email)}</div>
              </div>
              <div className="detail-group">
                <label className="detail-label">Linked Google Account</label>
                <div className="detail-value">
                  {patient.googleId ? patient.email : 'Not connected'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Onboarding Tab Component (unchanged)
// Onboarding Tab Component with Custom Layouts
const OnboardingTab = ({ patient, displayValue }) => {
  return (
    <div className="tab-panel">
      {/* Part 1: Personal Information - 3 COLUMNS */}
      <div className="onboarding-part-1">
        <div className="section-card">
          <h3 className="section-title">Part 1: Personal Information</h3>
          <div className="onboarding-grid">
          <div className="detail-group">
            <label className="detail-label">First Name</label>
            <div className="detail-value">{displayValue(patient.firstName)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Middle Name</label>
            <div className="detail-value">{displayValue(patient.middleName)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Last Name</label>
            <div className="detail-value">{displayValue(patient.lastName)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Nickname</label>
            <div className="detail-value">{displayValue(patient.nickname)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Birthdate</label>
            <div className="detail-value">
              {patient.birthdate ? new Date(patient.birthdate).toLocaleDateString() : 'Not provided'}
            </div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Age</label>
            <div className="detail-value">{displayValue(patient.age)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Gender</label>
            <div className="detail-value">{displayValue(patient.gender)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Pronouns</label>
            <div className="detail-value">{displayValue(patient.pronouns)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Location</label>
            <div className="detail-value">{displayValue(patient.location)}</div>
          </div>
        </div>
      </div>
      </div>

      {/* Part 2: Mental Health History - Sequential Layout */}
      <div className="onboarding-part-2">
        <div className="section-card">
          <h3 className="section-title">Part 2: Mental Health History</h3>
          <div className="onboarding-grid">
            <div className="detail-group">
              <label className="detail-label">Current Mental Health Diagnosis</label>
              <div className="detail-value">{displayValue(patient.diagnosis)}</div>
            </div>
            <div className="detail-group">
              <label className="detail-label">Treatment History</label>
              <div className="detail-value">{displayValue(patient.treatmentHistory)}</div>
            </div>
            <div className="detail-group">
              <label className="detail-label">Frequency Symptoms</label>
              <div className="detail-value">{displayValue(patient.symptomsFrequency)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Part 3: Doctor & Care Options - 3 COLUMNS */}
      <div className="onboarding-part-3">
        <div className="section-card">
          <h3 className="section-title">Part 3: Doctor & Care Options</h3>
          <div className="onboarding-grid">
            <div className="detail-group">
            <label className="detail-label">Sleep Patterns</label>
            <div className="detail-value">{displayValue(patient.sleepPatterns)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Exercise Frequency</label>
            <div className="detail-value">{displayValue(patient.exerciseFrequency)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Dietary Patterns</label>
            <div className="detail-value">{displayValue(patient.dietaryPatterns)}</div>
          </div>
        </div>
      </div>
      </div>

      {/* Part 5: Emergency Contact Person - Keep Original Grid */}
      <div className="section-card">
        <h3 className="section-title">Part 4: Emergency Contact Person</h3>
        <div className="onboarding-grid">
          <div className="detail-group">
            <label className="detail-label">Emergency Contact Name</label>
            <div className="detail-value">{displayValue(patient.emergencyName)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Relationship</label>
            <div className="detail-value">{displayValue(patient.emergencyRelationship)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Phone Number</label>
            <div className="detail-value">{displayValue(patient.emergencyPhone)}</div>
          </div>
          <div className="detail-group">
            <label className="detail-label">Email Address</label>
            <div className="detail-value">{displayValue(patient.emergencyEmail)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Mood Check-ins Tab Component
const MoodCheckinsTab = ({ 
  patient,
  patientId,  
  navigate,  
  moodData,
  moodLoading,
  moodError,
  selectedDays,
  setSelectedDays,
  fetchPatientMoodData,
  moodSvgUrls,
  moodColors
}) => {
  // Helper functions
  const formatMoodLabel = (moodKey) => {
    return moodKey?.charAt(0).toUpperCase() + moodKey?.slice(1) || 'Unknown';
  };

  const getMoodSvg = (moodKey) => {
    return moodSvgUrls[moodKey] || moodSvgUrls['okay'];
  };

  const getMoodColor = (moodKey) => {
    return moodColors[moodKey] || '#FFC107';
  };

  const formatDayName = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const getMoodLabelColor = (moodKey) => {
    const moodLabelColors = {
      great: '#3A8314',
      good: '#7A9241',
      okay: '#9A8C06',
      struggling: '#A26133',
      upset: '#944747'
    };
    return moodLabelColors[moodKey] || '#000000';
  };

  const getAllMoodsWithData = () => {
    const allMoods = ['great', 'good', 'okay', 'struggling', 'upset'];
    const result = {};
    
    allMoods.forEach(mood => {
      result[mood] = { count: 0, percentage: 0 };
    });
    
    if (moodDistribution) {
      Object.entries(moodDistribution).forEach(([key, data]) => {
        result[key] = data;
      });
    }
    
    return result;
  };

  if (moodLoading) {
    return (
      <div className="tab-panel">
        <div className="doctor-mood-loading-container">
          <div className="doctor-mood-loading-spinner"></div>
          <p>Loading mood check-ins...</p>
        </div>
      </div>
    );
  }

  if (moodError) {
    return (
      <div className="tab-panel">
        <div className="doctor-mood-error-container">
          <div className="doctor-mood-error-alert">
            <span className="doctor-mood-error-icon">⚠️</span>
            <span className="doctor-mood-error-message">{moodError}</span>
          </div>
          <button className="doctor-mood-retry-button" onClick={fetchPatientMoodData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!moodData || !moodData.keyMetrics) {
    return (
      <div className="tab-panel">
        <div className="doctor-mood-checkins-container">
          {/* Header */}
          <div className="doctor-mood-checkins-header">
            <div className="doctor-mood-header-right">
              <div className="doctor-mood-time-filter">
                <label>Show data for</label>
                <select 
                  value={selectedDays} 
                  onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                  className="doctor-mood-days-select"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                </select>
              </div>
              <div className="doctor-mood-rainbow-icon">🌈</div>
            </div>
          </div>

          <div className="doctor-mood-empty-state">
            <h3>No Mood Check-ins Available</h3>
            <p>{patient.firstName} hasn't submitted any mood check-ins during the selected time period.</p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              Mood check-ins will appear here once {patient.firstName} starts using the mood tracking feature.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { keyMetrics, dailyOverview, moodDistribution, moodHistory } = moodData;

  return (
    <div className="tab-panel">
      <div className="doctor-mood-checkins-container">
        {/* Header */}
        <div className="doctor-mood-checkins-header">
          <div className="doctor-mood-header-right">
            <div className="doctor-mood-time-filter">
              <label>Show data for</label>
              <select 
                value={selectedDays} 
                onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                className="doctor-mood-days-select"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
          </div>
        </div>

        {/* This Week's Mood Summary */}
        <div className="doctor-mood-weekly-insights-header">
          <h2>This Week's Mood Summary</h2>
        </div>

        {/* Key Metrics */}
        <div className="doctor-mood-key-metrics-section">
          <div className="doctor-mood-metrics-grid">
            <div className="doctor-mood-metric-card">
              <h3>Total Check-ins</h3>
              <div className="doctor-mood-metric-card-inside">
                <div className="doctor-mood-metric-value">{keyMetrics?.totalLogs || 0}</div>
                <div className="doctor-mood-metric-label">This Period</div>
              </div>
            </div>
            <div className="doctor-mood-metric-card">
              <h3>Average per Day</h3>
              <div className="doctor-mood-metric-card-inside">
                <div className="doctor-mood-metric-value">{keyMetrics?.averageLogsPerDay || 0}</div>
                <div className="doctor-mood-metric-label">Check-ins / day</div>
              </div>
            </div>
            <div className="doctor-mood-metric-card">
              <h3>Average Mood Score</h3>
              <div className="doctor-mood-metric-card-inside">
                <div className="doctor-mood-metric-value">{keyMetrics?.averageMoodScore || 0}</div>
                <div className="doctor-mood-metric-label">out of 5</div>
              </div>
            </div>
            <div className="doctor-mood-metric-card">
              <h3>Most Frequent Mood</h3>
              <div className="doctor-mood-emotional-trends">
                {keyMetrics?.topEmotionalTrends?.length > 0 ? (
                  <div className="doctor-mood-trend-item">
                    <div className="doctor-mood-emotional-trends-conainer-inside-emoji">
                      <img 
                        src={getMoodSvg(keyMetrics.topEmotionalTrends[0].mood?.toLowerCase())} 
                        alt={keyMetrics.topEmotionalTrends[0].mood}
                        className="doctor-mood-trend-svg"
                      />
                    </div>
                    <div className="doctor-mood-emotional-trends-conainer-inside">
                      <span className="doctor-mood-trend-label">{keyMetrics.topEmotionalTrends[0].mood}</span>
                    </div>
                  </div>
                ) : (
                  <div className="doctor-mood-trend-item">
                    <div className="doctor-mood-emotional-trends-conainer-inside-emoji">
                      <img src={getMoodSvg('okay')} alt="okay" className="doctor-mood-trend-svg" />
                    </div>
                    <div className="doctor-mood-emotional-trends-conainer-inside">
                      <span className="doctor-mood-trend-label">No data</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="doctor-mood-header-divider"></div>

        {/* Daily Overview */}
        {dailyOverview && dailyOverview.length > 0 && (
          <div className="doctor-mood-daily-overview-section">
            <h2>Daily Overview</h2>
            <div className="doctor-mood-daily-overview-grid">
              {dailyOverview.map((day, index) => (
                <div key={index} className="doctor-mood-daily-item">
                  <div className="doctor-mood-daily-date">{day.dateFormatted}</div>
                  <div className="doctor-mood-daily-day-name">{formatDayName(day.date)}</div>
                  <div className="doctor-mood-daily-mood-svg">
                    <img 
                      src={getMoodSvg(day.mostFrequentMood)} 
                      alt={day.mostFrequentMood}
                      className="doctor-mood-daily-svg"
                    />
                  </div>
                  <div className="doctor-mood-daily-stats">
                    <div className="doctor-mood-daily-score">{day.averageMoodScore}</div>
                    <div className="doctor-mood-daily-entries">{day.totalEntries} logs</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mood Distribution */}
        {moodDistribution && Object.keys(moodDistribution).length > 0 && (
          <div className="doctor-mood-distribution-section">
            <div className="doctor-mood-distribution-container">
              <h2>Mood Distribution</h2>
              <div className="doctor-mood-distribution-grid">
                {Object.entries(getAllMoodsWithData()).map(([moodKey, data]) => (
                  <div key={moodKey} className={`doctor-mood-distribution-item ${moodKey}`}>
                    <div className="doctor-mood-info">
                      <img 
                        src={getMoodSvg(moodKey)} 
                        alt={moodKey}
                        className="doctor-mood-svg-icon"
                      />
                      <span className="doctor-mood-label" style={{ color: getMoodLabelColor(moodKey) }}>
                        I'm {moodKey}
                      </span>
                    </div>
                    
                    <div className="doctor-mood-log-count-container">
                      <div className="doctor-mood-count">{data.count} logs</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="doctor-mood-header-divider"></div>

        {/* Mood History Table */}
        {moodHistory && moodHistory.length > 0 && (
          <div className="doctor-mood-history-section">
            <h2>Mood Check-in History</h2>
            
            <div className="doctor-mood-appointments-table">
              {/* Table Header */}
              <div className="doctor-mood-table-header">
                <div className="doctor-mood-header-cell">Date</div>
                <div className="doctor-mood-header-cell">Time</div>
                <div className="doctor-mood-header-cell">Mood</div>
                <div className="doctor-mood-header-cell">Rating</div>
                <div className="doctor-mood-header-cell">Reflection</div>
                <div className="doctor-mood-header-cell">Actions</div>
              </div>

              {/* Table Rows */}
              {moodHistory.map((entry, index) => (
                <div key={entry._id || index} className="doctor-mood-table-row">
                  <div className="doctor-mood-table-cell">
                    {new Date(entry.timestamp || entry.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="doctor-mood-table-cell">
                    {new Date(entry.timestamp || entry.createdAt).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                  <div className="doctor-mood-table-cell">
                    <div className="doctor-mood-indicator">
                      <img 
                        src={getMoodSvg(entry.moodKey || 'okay')} 
                        alt={entry.moodKey || 'okay'}
                        className="doctor-mood-svg-small"
                        style={{ width: '20px', height: '20px', marginRight: '8px' }}
                      />
                      <span>{formatMoodLabel(entry.moodKey)}</span>
                    </div>
                  </div>
                  <div className="doctor-mood-table-cell">
                    <div 
                      className="doctor-mood-rating"
                      style={{ 
                        background: `${getMoodColor(entry.moodKey)}20`,
                        color: getMoodColor(entry.moodKey),
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontWeight: '600',
                        display: 'inline-block'
                      }}
                    >
                      {entry.mood || entry.rating || 'N/A'}/5
                    </div>
                  </div>
                  <div className="doctor-mood-table-cell">
                    <div className="doctor-mood-reflection-preview">
                      {entry.reflection ? (
                        entry.reflection.length > 50 
                          ? `${entry.reflection.substring(0, 50)}...`
                          : entry.reflection
                      ) : (
                        <span style={{ color: '#999', fontSize: '12px' }}>No reflection</span>
                      )}
                    </div>
                  </div>
                  <div className="doctor-mood-table-cell">
  <div className="doctor-mood-actions-cell">
    <button
          className="doctor-mood-action-btn doctor-mood-view-btn"
          onClick={() => {
            navigate(`/doctor/patients/${patientId}/mood-analysis`, {
              state: {
                patientData: {
                  patientName: `${patient.firstName} ${patient.lastName}`,
                  patientEmail: patient.email,
                  registrationDate: patient.createdAt || patient.registrationDate
                }
              }
            });
          }}
        >
      <svg className="doctor-mood-button-icon" viewBox="0 0 24 24" fill="none">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#548170" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="3" stroke="#548170" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span className="doctor-mood-button-text">View</span>
    </button>
  </div>
</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientDetails;